import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Tab, Typography } from '@mui/material';
import { AsyncUploader } from 'components/AsyncUploader';
import { ButtonBack, ButtonCancel, ButtonConfirm } from "components/buttons";
import { MprcFilterAppBar } from "components/filter";
import { IconButtonClose } from 'components/iconButtons';
import { IconButtonExpandScreen } from 'components/iconButtons/IconButtonExpandScreen';
import { MprcBarTabs } from "components/MprcBarTabs";
import { CloudImage } from "components/MprcImageHandler/CloudImageSelection";
import { LocalImage } from "components/MprcImageHandler/LocalImageSelection";
import { useMprcTheme } from "components/MprcThemeProvider";
import { GoogleSearchImageItem } from "generated/CoreApi_v1";
import { TFunction } from 'i18next';
import { enqueueSnackbar, useSnackbar } from "notistack";
import * as React from 'react';
import { useState } from "react";
import { Accept } from 'react-dropzone';
import "react-image-crop/dist/ReactCrop.css";
import { ImageSelectionBackgroundType, ImageSelectionFit, ImageSelectionResult, ImageSelectionSolidBackgroundColor, TabOptions } from '.';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderMenu {
  category: string;
  onChange: (event: React.ChangeEvent<{}>, value: any) => void;
}

const RenderMenuBar: React.FC<IRenderMenu> = (props) => {
  const { theme } = useMprcTheme();
  return (
    <MprcFilterAppBar>
      <MprcBarTabs
        variant="fullWidth"
        value={props.category}
        onChange={props.onChange}
      >
        <Tab value="library" label={theme.MprcImageSelectionDialog.tabLibraryText} />
        <Tab value="local" label={theme.MprcImageSelectionDialog.tabUploadImageText} />
      </MprcBarTabs>
    </MprcFilterAppBar>
  );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function onImageSelectionUploadError(instance: AsyncUploader, thrown: any, t: TFunction) {

  if (instance.isCancel(thrown)) {
    enqueueSnackbar(t("AsyncUpload.UploadCanceled", { ns: "shared" }), { variant: "info" });
  }
  else {
    let message = t("AsyncUpload.UploadError", { ns: "shared" });
    if (thrown.storageExceeded == true) {
      message = t("AsyncUpload.StorageExceededMessage", { ns: "shared" });
    }
    else
      if (thrown.handled == false) {
        var e = (thrown.handledError) as ApiProblemDetails;
        if (e.status == 409) message = e.detail ?? "";
      }
    enqueueSnackbar(message, { variant: "error" });
  }
}

interface IProps {
  allowEdit: boolean;
  maxSize?: number;
  dimension?: ImageDimension;
  adjustImageDimensionToRatio?: boolean;
  backgroundType: ImageSelectionBackgroundType;
  initialSource: TabOptions;
  acceptedFiles?: Accept;
  initialFile?: File;
  initialSearch?: string;
  fitMode?: ImageSelectionFit;
  onClose: (result: ImageSelectionResult | null) => void;
}

export const MprcImageSelectionDialog = (props: IProps) => {
  ////////////////////////////////////////////////////////////////////////////
  const { theme } = useMprcTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [tabSelected, setTabSelected] = useState<TabOptions>(props.initialSearch ? "library" : (props.initialSource ?? "local"));
  const [fileResult, setFileResult] = useState<ImageSelectionResult | null>(null);
  const { fitMode, acceptedFiles, dimension, adjustImageDimensionToRatio, allowEdit, maxSize, backgroundType, onClose } = props;

  const [cloudSelectedItem, setCloudSelectedItem] = useState<GoogleSearchImageItem | null>(null);
  const [localSelectedItem, setLocalSelectedItem] = useState<File | null>(null);
  const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
  ////////////////////////////////////////////////////////////////////////////

  const handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
    setTabSelected(value);
  };

  const handleFileChanged = (result: ImageSelectionResult | null) => {
    setFileResult(result);
  };

  const handleError = (message: string) => {
    enqueueSnackbar(message, { variant: "error" });
  };

  ////////////////////////////////////////////////////////////////////////////

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      role="dialog"
      fullScreen={fullScreenMode}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography noWrap variant="h2">{theme.MprcImageSelectionDialog.dialogTitle}</Typography>
          <div>
            <IconButtonExpandScreen
              expanded={fullScreenMode}
              size="small"
              onClick={() => setFullScreenMode(!fullScreenMode)}
            />
            <IconButtonClose
              color="inherit"
              size="small"
              onClick={() => onClose(null)}
            />
          </div>
        </Stack>
      </DialogTitle>
      <DialogContent style={{ height: 500 }}>
        <Grid container direction="column" wrap="nowrap" height="100%" >
          {
            ((tabSelected == "library" && cloudSelectedItem == null) || (tabSelected == "local" && localSelectedItem == null)) &&
            <Grid>
              <RenderMenuBar {...props} category={tabSelected} onChange={handleTabSelection} />
            </Grid>
          }

          <Grid size="grow">
            {
              tabSelected == "library" &&
              <CloudImage
                fitMode={fitMode}
                size={fullScreenMode ? "large" : "small"}
                searchTerm={props.initialSearch}
                acceptedFiles={acceptedFiles}
                allowEdit={allowEdit}
                dimension={dimension}
                backgroundColor={backgroundType == "solid" ? ImageSelectionSolidBackgroundColor : "transparent"}
                adjustImageDimensionToRatio={adjustImageDimensionToRatio}
                maxSize={maxSize ?? null}
                onChange={handleFileChanged}
                onError={(message) => { handleError(message); setCloudSelectedItem(null); }}
                selectedImage={cloudSelectedItem}
                onSelectImage={(image) => setCloudSelectedItem(image)}
              />
            }

            {
              tabSelected == "local" &&
              <LocalImage
                fitMode={fitMode}
                acceptedFiles={acceptedFiles}
                allowEdit={allowEdit}
                dimension={dimension}
                backgroundColor={backgroundType == "solid" ? ImageSelectionSolidBackgroundColor : "transparent"}
                adjustImageDimensionToRatio={adjustImageDimensionToRatio}
                maxSize={maxSize ?? null}
                onChange={handleFileChanged}
                onError={(message) => { handleError(message); setLocalSelectedItem(null); }}
                selectedFile={localSelectedItem || props.initialFile || null}
                onSelectFile={(file) => setLocalSelectedItem(file)}
              />
            }
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions disableSpacing>

        <Grid container alignItems="center" spacing={1} width="100%">
          <Grid size="grow">
            {
              tabSelected == "library" && cloudSelectedItem &&
              <ButtonBack
                color="warning"
                variant="contained"
                onClick={() => setCloudSelectedItem(null)}
              />
            }
            {
              tabSelected == "local" && localSelectedItem &&
              <ButtonBack
                color="warning"
                variant="contained"
                onClick={() => setLocalSelectedItem(null)}
              />
            }
          </Grid>

          <Grid>
            <ButtonCancel
              variant="contained"
              onClick={() => onClose(null)}
            />
          </Grid>

          {
            fileResult &&
            <Grid>
              <ButtonConfirm
                variant="contained"
                onClick={() => onClose(fileResult)}
              />
            </Grid>
          }
        </Grid>
      </DialogActions>

    </Dialog >
  );
};
